import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useWallet } from '@/hooks/useWallet';
import { walletDisplayTotals } from '@/lib/api/wallet';
import { formatCreditsShort, planWorkload } from '@/lib/writer/telemetryCost';
import { cn } from '@/lib/utils';

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Credits translated into work, using the same helpers pricing v2 reads, so
 * the wording can never drift from the meter.
 */
const WalletPanel = () => {
  const { data: wallet, isLoading } = useWallet();

  if (isLoading) {
    return (
      <section className="mb-12" aria-label="Credits" aria-busy="true">
        <div className="h-[184px] rounded-lg border border-border/60 bg-card" />
      </section>
    );
  }

  if (!wallet) {
    return (
      <section className="mb-12 border-y border-border/60 py-5" aria-label="Credits">
        <p className="text-sm text-muted-foreground">
          Credit balance is temporarily unavailable.
        </p>
      </section>
    );
  }

  const workload = planWorkload(wallet.availableCredits);
  const resets = formatDate(wallet.periodEnd);
  const rolloverExpiry = formatDate(wallet.rolloverExpiresAt);
  const totals = walletDisplayTotals(wallet);
  const hasDetails = wallet.rolloverCredits > 0 || wallet.topupCredits > 0 || totals.reserved > 0;

  return (
    <section className="mb-12" aria-labelledby="credit-overview-title">
      <div className="rounded-lg border border-border/60 bg-card px-5 py-5 sm:px-6 sm:py-6">
        <div className="flex items-center justify-between gap-4">
          <h2
            id="credit-overview-title"
            className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
          >
            Credits
          </h2>
          <Link to="/pricing-v2" className="text-xs font-medium text-primary hover:underline">
            Plans and top-ups
          </Link>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 sm:max-w-xl sm:gap-10">
          <div className="min-w-0">
            <div
              className={cn(
                'text-3xl font-semibold tracking-normal tabular-nums text-foreground sm:text-4xl',
                totals.isLow && 'text-destructive',
              )}
            >
              {formatCreditsShort(totals.remaining)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">remaining</div>
          </div>
          <div className="min-w-0 border-l border-border/60 pl-4 sm:pl-10">
            <div className="text-2xl font-medium tracking-normal tabular-nums text-foreground sm:text-3xl">
              {formatCreditsShort(totals.used)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">used this period</div>
          </div>
        </div>

        <div
          className="mt-5 flex h-1.5 w-full overflow-hidden rounded-full bg-secondary"
          role="progressbar"
          aria-label="Credits used this period"
          aria-valuemin={0}
          aria-valuemax={totals.total}
          aria-valuenow={totals.used}
          aria-valuetext={`${formatCreditsShort(totals.used)} used, ${formatCreditsShort(totals.remaining)} remaining${totals.reserved > 0 ? `, ${formatCreditsShort(totals.reserved)} reserved` : ''}`}
        >
          {totals.usedPercent > 0 && (
            <span
              className="h-full bg-muted-foreground/55 motion-safe:transition-[width] motion-safe:duration-500"
              style={{ width: `${totals.usedPercent}%` }}
              aria-hidden="true"
            />
          )}
          {totals.remainingPercent > 0 && (
            <span
              className={cn(
                'h-full bg-primary motion-safe:transition-[width] motion-safe:duration-500',
                totals.isLow && 'bg-destructive',
              )}
              style={{ width: `${totals.remainingPercent}%` }}
              aria-hidden="true"
            />
          )}
          {totals.reservedPercent > 0 && (
            <span
              className="h-full bg-muted-foreground/25 motion-safe:transition-[width] motion-safe:duration-500"
              style={{ width: `${totals.reservedPercent}%` }}
              aria-hidden="true"
            />
          )}
        </div>

        <div className="mt-4 flex flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2">
          <span>{wallet.planName} plan</span>
          {resets && <span className="before:mr-2 before:hidden before:content-['·'] sm:before:inline">Renews {resets}</span>}
          <span className="sm:basis-full">
            About {workload.writingTurns} writing turn{workload.writingTurns === 1 ? '' : 's'} or{' '}
            {workload.reviews} full AI review{workload.reviews === 1 ? '' : 's'} remain
          </span>
        </div>

        {hasDetails && (
          <details className="group mt-4 border-t border-border/60 pt-3">
            <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
              Details
              <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" aria-hidden="true" />
            </summary>
            <dl className="grid gap-3 pb-1 text-xs sm:grid-cols-3">
              {wallet.rolloverCredits > 0 && (
                <div>
                  <dt className="text-muted-foreground">Carried over</dt>
                  <dd className="mt-0.5 font-medium text-foreground">
                    {formatCreditsShort(wallet.rolloverCredits)} credits
                    {rolloverExpiry ? `, expires ${rolloverExpiry}` : ''}
                  </dd>
                </div>
              )}
              {wallet.topupCredits > 0 && (
                <div>
                  <dt className="text-muted-foreground">Top-ups</dt>
                  <dd className="mt-0.5 font-medium text-foreground">
                    {formatCreditsShort(wallet.topupCredits)} credits, never expire
                  </dd>
                </div>
              )}
              {totals.reserved > 0 && (
                <div>
                  <dt className="text-muted-foreground">Reserved</dt>
                  <dd className="mt-0.5 font-medium text-foreground">
                    {formatCreditsShort(totals.reserved)} credits for work in progress
                  </dd>
                </div>
              )}
            </dl>
          </details>
        )}
      </div>
    </section>
  );
};

export default WalletPanel;
